import React from "react";
import DataComponent, {executeComponentCallback} from 'Core/components/DataComponent';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import {selectors} from 'Core/store/reducers';
import {reducerStoreKey} from './reducer';
import {reducerStoreKey as popupReducerStoreKey} from '../../../../reducer';
import {reducerStoreKey as pageReducerStoreKey} from '../../../../../../reducer';
import * as pageConfig from 'Pages/apps/default/customerGroups/config';
import {getGlobalActions} from 'Core/helpers/redux';
import * as actions from './actions';
import * as filterDataMap from './dataMap/filter';
import {areAllObjectPropsEmpty, getArray, getNumber, getString, isset, trimArray} from 'Core/helpers/data';
import {scrollToSelector} from 'Core/helpers/dom';
import {Tooltip} from 'react-tippy';
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from 'Core/components/display/Button';
import {icon_font_delete_symbol} from 'Config/app';
import SimpleStaticSearch, {
	SIMPLE_STATIC_SEARCH_DISPLAY_TYPE,
	SIMPLE_STATIC_SEARCH_LAYOUT,
	SimpleStaticSearchOptionObject
} from 'Core/components/advanced/SimpleStaticSearch';
import {CUSTOMER_GROUP_IMPORT_CUSTOMERS_TYPES} from './const';
import PhoneInput from 'Core/components/input/PhoneInput';
import CustomerCustomFieldFilter from 'Pages/apps/default/customers/components/CustomerCustomFieldFilter';
import {FORM_FIELD_LABEL_POSITION} from 'Core/components/advanced/FormWrapper/FormField';
import Label from 'Core/components/display/Label';
import DataTable, {DATA_TABLE_CELL_TYPE} from 'Core/components/advanced/DataTable';
import {
	DataTableCellAnyTypeOptionsDataObject,
	DataTableCellDateTypeOptionsDataObject,
	DataTableCellTextTypeOptionsDataObject
} from 'Core/components/advanced/DataTable/DataTableCell/dataObjects';
import {getAppLocaleDateFormat} from 'Core/helpers/locale';
import {LOCALE_DATE_FORMAT_NAME} from 'Core/const/locale';
import {PAGINATION_TYPE} from 'Core/components/action/Pagination';
import Acl from 'Core/acl';
import ProjectsACL from 'Acl/projects';
import {cloneDeep, isEqual} from 'lodash';
import ConfirmDialog from 'Core/components/dialogs/ConfirmDialog';
import {hideLoading, showLoading} from 'Core/helpers/loading';
import {isSuccessful} from 'Core/helpers/io';
import ImportCustomersFromFileDialog from 'Components/dialogs/ImportCustomersFromFileDialog';
import {IMPORT_CUSTOMERS_FROM_FILE_TYPE} from 'Components/advanced/ImportCustomersFromFile/const';

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in a component.
 */
const mapStateToProps = state => ({
	customerGroupsItemId: getString(selectors[pageReducerStoreKey].getCustomerGroupsItem(state), 'id'),
	customerGroupsItemName: getString(selectors[pageReducerStoreKey].getCustomerGroupsItem(state), 'name'),
	customerGroupsItemProjectId: getString(selectors[pageReducerStoreKey].getCustomerGroupsItem(state), 'projectId'),
	mainList: selectors[reducerStoreKey].getCustomersList(state),
	mainListPagination: selectors[reducerStoreKey].getCustomersListPagination(state),
	mainListSort: selectors[reducerStoreKey].getCustomersListSort(state),
	mainListFilter: selectors[reducerStoreKey].getCustomersListFilter(state),
	inProgressChangeRequests: selectors[popupReducerStoreKey].getInProgressChangeRequests(state),
});

class CustomerGroupImportCustomersList extends DataComponent {
	constructor(props) {
		super(props, {
			data: {
				/**
				 * List of selected items in the main list
				 * @type {CustomerGroupImportCustomersListCustomerDataObject[]}
				 */
				selection: [],
			},

			/**
			 * Customer custom fields
			 * @type {
			 * 	{id: number, name: string, code: string, description: string, type: CustomerCustomFiledType}[]
			 * 	|null|undefined
			 * }
			 */
			customerCustomFields: null,
		}, {
			translationPath: `${pageConfig.translationPath}.ItemPopup.CustomersTab.customers_section`, 
			domPrefix: 'customer-group-import-customers-list',
			disableLoad: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onChange', 'onEnterKey'],
		});

		// Refs
		this.mainListRef = null;
		this.mainListFilterRef = null;

		// Data methods
		this.getCustomerGroupId = this.getCustomerGroupId.bind(this);
		this.getCustomerGroupName = this.getCustomerGroupName.bind(this);
		this.reloadMainList = this.reloadMainList.bind(this);
		this.loadMainListPage = this.loadMainListPage.bind(this);
		this.sortMainList = this.sortMainList.bind(this);
		this.filterMainList = this.filterMainList.bind(this);
		this.removeMainListFilter = this.removeMainListFilter.bind(this);
		this.isMainListFilterEmpty = this.isMainListFilterEmpty.bind(this);

		// Action methods
		this.handleMainListSelectionChange = this.handleMainListSelectionChange.bind(this);
		this.deleteMainListItem = this.deleteMainListItem.bind(this);
		this.deleteMainListItems = this.deleteMainListItems.bind(this);
		this.deleteMainListItemFromFile = this.deleteMainListItemFromFile.bind(this);

		// Render methods
		this.renderActions = this.renderActions.bind(this);
	}

	async asyncComponentDidMount(override = false) {
		await super.asyncComponentDidMount(override);
		
		const {loadCustomerGroupCustomerListAction, fetchCustomerCustomFieldsAction} = this.props;
		const customerGroupId = this.getCustomerGroupId();
		
		if (!!customerGroupId) {
			// Initial load of the customer list
			await this.executeAbortableActionMount(loadCustomerGroupCustomerListAction, customerGroupId);

			// Load customer custom field into a local state
			await this.executeAbortableActionMount(fetchCustomerCustomFieldsAction)
				.then(ccf => this.setState({customerCustomFields: ccf}));
		}
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		return super.componentDidUpdate(prevProps, prevState, snapshot)
			.then(state => {
				const {loadCustomerGroupCustomerListAction, clearCustomerGroupCustomerListAction} = this.props;
				const customerGroupId = this.getCustomerGroupId();
				const prevCustomerGroupId = this.getCustomerGroupId(prevProps);
				const inProgressChangeRequests = getArray(this.props, 'inProgressChangeRequests');
				const prevInProgressChangeRequests = getArray(prevProps, 'inProgressChangeRequests');
				
				if (customerGroupId !== prevCustomerGroupId) {
					// Clear the main list if a customer group is unselected
					// @note This is done in case this tab is not removed in time when switching from edit to create a popup.
					if (!customerGroupId) {
						clearCustomerGroupCustomerListAction();
						return Promise.resolve(state);
					}

					// Do nothing if the previous customer group ID is empty since this case is handled by the mount method
					if (!prevCustomerGroupId) {
						return Promise.resolve(state);
					}
					
					// Load the main list if a new customer group is selected
					return this.setState(cloneDeep(this.initialState))
						.then(() => this.executeAbortableAction(loadCustomerGroupCustomerListAction, customerGroupId));
				}

				// Reload the main list if the list of customer group change requests that are in progress changed
				if (!isEqual(inProgressChangeRequests, prevInProgressChangeRequests)) this.reloadMainList(false).then();
				
				return Promise.resolve(state);
			});
	}
	
	componentWillUnmount() {
		super.componentWillUnmount();
		
		const {resetCustomerGroupImportCustomersListActions} = this.props;
		resetCustomerGroupImportCustomersListActions();
	}


	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Retrieves the customer group ID based on the available properties
	 * 
	 * @param {Object} [props] - Props to use for getting the value. If not specified, current props will be used.
	 * @return {string}
	 */
	getCustomerGroupId(props) {
		const propsToUse = (isset(props) ? props : this.props);
		const {customerGroupsItemId, customerGroupId} = propsToUse;
		return (isset(customerGroupId) ? customerGroupId : customerGroupsItemId);
	}

	/**
	 * Retrieves the customer group name based on the available properties
	 *
	 * @param {Object} [props] - Props to use for getting the value. If not specified, current props will be used.
	 * @return {string}
	 */
	getCustomerGroupName(props) {
		const propsToUse = (isset(props) ? props : this.props);
		const {customerGroupsItemName, customerGroupName} = propsToUse;
		return (isset(customerGroupName) ? customerGroupName : customerGroupsItemName);
	}
	
	/**
	 * Reload the main list using current options (page, sort, ...)
	 *
	 * @param {boolean} [loadingOverlay=true] - Flag that specifies if loading overlay should be visible.
	 * @return {Promise<*>}
	 */
	reloadMainList(loadingOverlay = true) {
		const {loadCustomerGroupCustomerListAction, mainListPagination, mainListSort, mainListFilter} = this.props;
		const customerGroupId = this.getCustomerGroupId();
		const {pageNo, perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomerGroupCustomerListAction, 
			customerGroupId, oFilter, (!!pageNo ? pageNo : undefined), perPage, sortBy, sortDir, loadingOverlay
		)
			.then(res => {
				this.mainListFilterRef?.reload();
				return res;
			})
			// Go to the previous page if there are no table rows after reload since some items might have been removed
			.then(() => {
				const mainList = getArray(this.props, 'mainList');
				const pageNo = getNumber(this.props, 'mainListPagination.pageNo', 1);
				if (mainList.length === 0 && pageNo > 1) return this.loadMainListPage(pageNo - 1);
			});
	}

	/**
	 * Reload the main list using current options (page, sort, ...) if any
	 * @param {number} [pageNo=1] - Page number to load (starts with 1).
	 * @return {Promise<*>}
	 */
	loadMainListPage(pageNo = 1) {
		const {loadCustomerGroupCustomerListAction, mainListPagination, mainListSort, mainListFilter} = this.props;
		const customerGroupId = this.getCustomerGroupId();
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomerGroupCustomerListAction, customerGroupId, oFilter, pageNo, perPage, sortBy, sortDir
		);
	}

	/**
	 * Sort main list
	 * @param {string} sortBy - Name of the sort column.
	 * @param {string} sortDir - Direction of the sort.
	 * @return {Promise<*>}
	 */
	sortMainList(sortBy, sortDir) {
		const {loadCustomerGroupCustomerListAction, mainListPagination, mainListFilter} = this.props;
		const customerGroupId = this.getCustomerGroupId();
		const {pageNo, perPage} = mainListPagination;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomerGroupCustomerListAction, customerGroupId, oFilter, pageNo, perPage, sortBy, sortDir
		);
	}

	/**
	 * Filter main list
	 * @param {Object} filter - Filter object where keys are filter field names and values are filter values.
	 * @return {Promise<*>}
	 */
	filterMainList(filter) {
		const {loadCustomerGroupCustomerListAction, mainListPagination, mainListSort} = this.props;
		const customerGroupId = this.getCustomerGroupId();
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(filter);

		return this.executeAbortableAction(
			loadCustomerGroupCustomerListAction, customerGroupId, oFilter, 1, perPage, sortBy, sortDir
		)
			.then(() => {
				if (areAllObjectPropsEmpty(oFilter, true, false)) {
					if (this.mainListFilterRef) this.mainListFilterRef.close();
				} else {
					scrollToSelector('#main-tab-table', false, 80);
				}
			});
	}

	/**
	 * Remove the main list filter
	 * @return {Promise<*>}
	 */
	removeMainListFilter() {
		return this.filterMainList(null);
	}

	/**
	 * Check if the main list filter is applied
	 * @return {Boolean}
	 */
	isMainListFilterEmpty() {
		return areAllObjectPropsEmpty(this.getProp('mainListFilter'), true, false);
	}


	// Action methods ---------------------------------------------------------------------------------------------------
	/**
	 * Handle main list selection change
	 * @return {Promise<Object>}
	 */
	handleMainListSelectionChange(selection) {
		return this.setValue('selection', selection);
	}

	/**
	 * Delete a single main list item
	 *
	 * @param {CustomerGroupImportCustomersListItemDataObject} item - Main list item to delete.
	 * @return {Promise<*>}
	 */
	deleteMainListItem(item) {
		const {
			openDialogAction, closeDialogAction, addSuccessMessageAction, deleteCustomerGroupCustomerListItemAction, 
		} = this.props;
		const customerGroupId = this.getCustomerGroupId();
		const customerGroupName = this.getCustomerGroupName();
		
		return new Promise(resolve => {
			const dialogGUIID = openDialogAction('', ConfirmDialog, {
				yesTimer: 3,
				message: this.t('confirm_delete', '', '', {
					name: `${item.customer.firstName} ${item.customer.lastName}`,
					groupName: customerGroupName,
				}),
				supportHtml: true,
				onYes: () => {
					const loading = showLoading(`#main-tab-table .actions`);
					this.executeAbortableAction(deleteCustomerGroupCustomerListItemAction, item.id, customerGroupId)
						.then(response => {
							if (isSuccessful(response)) {
								addSuccessMessageAction(this.t('delete_success_msg'));

								// Remove the deleted item from the main list selection
								if (this.mainListRef) this.mainListRef.removeFromSelection(item);
								this.setValue('selection', this.getValue('selection').filter(i => i.id !== item.id)).then();
								
								// Reload main list data
								// @note This is done asynchronously on purpose because there is no reason to wait for this to 
								// finish before continuing. 
								this.reloadMainList().then();
							}
							return Promise.resolve(response);
						})
						.then(() => closeDialogAction(dialogGUIID))
						.finally(() => {
							hideLoading(loading);
							resolve();
						});
				},
				onNo: () => {
					closeDialogAction(dialogGUIID);
					resolve();
				}
			}, {
				id: 'customer-group-customer-item-delete-dialog',
				closeOnEscape: true,
				closeOnClickOutside: true,
				hideCloseBtn: true,
				maxWidth: 460
			});
			this.setOption(
				'dialogsToCloseOnUnmount',
				trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
			);
		});
	}

	/**
	 * Delete selected main list items
	 * @return {Promise<any>}
	 */
	deleteMainListItems() {
		const {
			openDialogAction, closeDialogAction, addSuccessMessageAction, deleteCustomerGroupCustomerListItemsAction, 
		} = this.props;
		const customerGroupId = this.getCustomerGroupId();
		const customerGroupName = this.getCustomerGroupName();
		const selection = this.getValue('selection');
		
		return new Promise(resolve => {
			const dialogGUIID = openDialogAction('', ConfirmDialog, {
				yesTimer: 3,
				message: this.t('confirm_delete_multiple', '', '', {
					count: selection.length,
					groupName: customerGroupName,
				}),
				supportHtml: true,
				onYes: () => {
					const loading = showLoading(`#main-page-table .actions`);
					this.executeAbortableAction(
						deleteCustomerGroupCustomerListItemsAction, selection.map(i => i.id), customerGroupId
					)
						.then(response => {
							if (isSuccessful(response)) {
								addSuccessMessageAction(this.t('delete_multiple_success_msg', '', '', {
									count: selection.length,
								}));
								
								// Clear main list selection
								if (this.mainListRef) this.mainListRef.clearSelection();
								this.setValue('selection', []).then();
								
								// Reload main list data
								// @note This is done asynchronously on purpose because there is no reason to wait for this to 
								// finish before continuing. 
								this.reloadMainList().then();
							}
							return Promise.resolve(response);
						})
						.then(() => closeDialogAction(dialogGUIID))
						.finally(() => {
							hideLoading(loading);
							resolve();
						});
				},
				onNo: () => {
					closeDialogAction(dialogGUIID);
					resolve();
				}
			}, {
				id: 'customer-group-customer-items-delete-dialog',
				closeOnEscape: true,
				closeOnClickOutside: true,
				hideCloseBtn: true,
				maxWidth: 600
			});
			this.setOption(
				'dialogsToCloseOnUnmount',
				trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
			);
		}); 
	}
	
	/**
	 * Delete items from the main list based on an uploaded file 
	 */
	deleteMainListItemFromFile() {
		const {projectId, customerGroupsItemProjectId, openDialogAction, addSuccessMessageAction} = this.props;
		const customerGroupId = this.getCustomerGroupId();
		const projectIdToUse = (isset(projectId) ? projectId : customerGroupsItemProjectId);
		
		const dialogGUIID = openDialogAction('', ImportCustomersFromFileDialog, {
			projectIds: [projectIdToUse],
			icon: 'trash',
			title: this.t('remove_from_file_title'),
			description: this.t('remove_from_file_desc'),
			importType: IMPORT_CUSTOMERS_FROM_FILE_TYPE.GROUP_REMOVE,
			showUniqueIdColumn: true,
			customerGroupId: customerGroupId,
			defaultOverride: false,
			loadingSelector: '#remove-customers-from-file-dialog',
			onSuccess: () => {
				addSuccessMessageAction(this.t('remove_form_file_success_msg'));

				// Clear main list selection
				if (this.mainListRef) this.mainListRef.clearSelection();
				this.setValue('selection', []).then();

				executeComponentCallback(this.props.onRemoveFromFileSuccess);
			},
		}, {
			id: 'remove-customers-from-file-dialog',
			className: 'bordered-title',
			closeOnEscape: true,
			closeOnClickOutside: false,
			hideCloseBtn: false,
			maxWidth: 640
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}
	
	
	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Render data table actions cell
	 * @param {CustomerGroupImportCustomersListItemDataObject} row - Data table row.
	 * @return {JSX.Element}
	 */
	renderActions(row) {
		return (
			<div className="actions standard-actions-row">
				<Tooltip
					className={`delete-main-list-item`}
					tag="div"
					title={`${this.t('Delete', 'general')} ...`}
					size="small"
					position="top-center"
					arrow={true}
					interactive={false}
				>
					<Button
						className={`action-btn hover-as-action`}
						displayStyle={BUTTON_STYLE.DEFAULT}
						displayType={BUTTON_DISPLAY_TYPE.NONE}
						icon={icon_font_delete_symbol}
						onClick={() => this.deleteMainListItem(row)}
					/>
				</Tooltip>
			</div>
		);
	}
	
	render() {
		// Do not render component if 'canRender' returns false
		if (!this.canRender()) return null;

		const {
			className, projectId, readOnly, hideSearch, mainList, mainListPagination, mainListSort, mainListFilter
		} = this.props;
		const canEdit = (
			!!projectId ?
				ProjectsACL.checkPermission(ProjectsACL, projectId, ['CUSTOMER_WRITE']) :
				Acl.checkPermission(Acl, ['CUSTOMER_WRITE'])
		);
		const {customerCustomFields} = this.state;
		const selection = getArray(this.getValue('selection'));
		
		return (
			<div id={this.getDomId()} className={`${this.getOption('domPrefix')} ${className}`}>
				{!readOnly ?
					<div className="toolbar standard for-table">
						{canEdit ?
							<>
								<Button
									icon={icon_font_delete_symbol}
									label={this.t('Remove selected', 'general')}
									displayType={BUTTON_DISPLAY_TYPE.NONE}
									onClick={this.deleteMainListItems}
									disabled={selection.length === 0}
								/>
								<div className="separator"/>
								<Button
									icon={icon_font_delete_symbol}
									label={this.t('remove_from_file_label')}
									displayType={BUTTON_DISPLAY_TYPE.NONE}
									onClick={this.deleteMainListItemFromFile}
									disabled={getArray(mainList).length === 0}
								/>
								<div className="separator"/>
							</>
							: null
						}
						<div className="spacer"/>
						<Tooltip
							tag="div"
							title={this.t('Reload data', 'general')}
							size="small"
							position="top-center"
							arrow={true}
							interactive={false}
						>
							<Button
								big={true}
								icon="refresh"
								displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
								displayStyle={BUTTON_STYLE.SUBTLE}
								onClick={() => this.reloadMainList()}
							/>
						</Tooltip>
					</div>
					: null
				}

				{!hideSearch ?
					<SimpleStaticSearch
						styleName="compact"
						className="main-search rows-4"
						defaultCollapse={true}
						layout={SIMPLE_STATIC_SEARCH_LAYOUT.STACKED}
						buttonProps={{
							displayStyle: BUTTON_STYLE.DEFAULT
						}}
						options={[
							new SimpleStaticSearchOptionObject(
								'externalCrmId',
								this.t('crmIdField')
							),
							new SimpleStaticSearchOptionObject(
								'firstName',
								this.t('firstNameField')
							),
							new SimpleStaticSearchOptionObject(
								'lastName',
								this.t('lastNameField')
							),
							new SimpleStaticSearchOptionObject(
								'importType',
								this.t('importTypeField'),
								SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.SELECT,
								{
									isClearable: true,
									options: CUSTOMER_GROUP_IMPORT_CUSTOMERS_TYPES.map(t => ({
										label: this.tt(t, 'importType'),
										value: t
									}))
								}
							),
							new SimpleStaticSearchOptionObject(
								'mobilePhoneNumber',
								this.t('phoneField'),
								SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.CUSTOM,
								{disableDropdown: false},
								PhoneInput,
								undefined,
								''
							),
							new SimpleStaticSearchOptionObject(
								'email',
								this.t('emailField')
							),
							new SimpleStaticSearchOptionObject(
								'customerCustomFields',
								'',
								SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.CUSTOM,
								{options: getArray(customerCustomFields).map(i => ({label: i.name, value: i.name}))},
								CustomerCustomFieldFilter,
								{
									labelPosition: FORM_FIELD_LABEL_POSITION.NONE,
									inputClassName: 'customer-custom-field-item custom-field-item'
								}
							),
						]}
						value={mainListFilter}
						title={(<Label icon="search" content={this.t('Search', 'general')} />)}
						applied={!this.isMainListFilterEmpty()}
						enableToolbar={true}
						enableResetButton={false}
						onChange={this.filterMainList}
						onRemove={this.removeMainListFilter}
						ref={node => { this.mainListFilterRef = node; }}
					/>
					: null
				}

				<DataTable
					id="main-tab-table"
					className={`standard ${canEdit && !readOnly ? 'sticky-last-column' : ''}`}
					responsive={true}
					selectable={canEdit && !readOnly}
					primaryKeyColumn="id"
					highlightOnHover={true}
					columns={[
						{
							name: 'customer.crmId',
							label: this.t('crmIdField'),
							dataTypeOptions: new DataTableCellTextTypeOptionsDataObject({
								whiteSpace: 'nowrap',
							}),
							defaultValue: '—',
							width: 1,
						},
						{
							name: 'customer.firstName',
							sortName: 'customerFirstName',
							label: this.t('firstNameField')
						},
						{
							name: 'customer.lastName',
							sortName: 'customerLastName',
							label: this.t('lastNameField'),
						},
						{
							name: 'customer.dateOfBirth',
							label: this.t('dateOfBirthField'),
							dataType: DATA_TABLE_CELL_TYPE.DATE,
							dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
								outputFormat: getAppLocaleDateFormat(LOCALE_DATE_FORMAT_NAME.SHORT),
								whiteSpace: 'nowrap',
							}),
							defaultValue: '—',
						},
						{
							name: 'customer.mobilePhoneNumber',
							label: this.t('phoneField'),
							defaultValue: '—',
						},
						{
							name: 'customer.email',
							label: this.t('emailField'),
							defaultValue: '—',
						},
						{
							name: 'importType',
							sortName: 'importType',
							label: this.t('importTypeField'),
							dataTypeOptions: new DataTableCellTextTypeOptionsDataObject({
								translatePath: `${this.getOption('translationPath')}.importType`,
							}),
						},

						{
							dataType: DATA_TABLE_CELL_TYPE.ANY,
							dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
								standardWrapper: false,
								content: this.renderActions,
							}),
							stopPropagation: true,
							width: 1,
							hide: !canEdit || readOnly,
						}
					]}
					data={mainList}
					paginationType={PAGINATION_TYPE.STATIC}
					onSortByColumn={this.sortMainList}
					onPaginationClick={this.loadMainListPage}
					onSelect={this.handleMainListSelectionChange}
					{...mainListPagination}
					{...mainListSort}
					ref={node => { this.mainListRef = node; }}
				/>
			</div>
		);
	}
}

/**
 * Define a component's own props that can be passed to it by parent components
 */
CustomerGroupImportCustomersList.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,
	// ID of the project 
	// @description If project ID is specified, this popup is used in the project-related page.
	projectId: PropTypes.string,
	// ID of the customer group to use instead of the one from a selected group item in Redux store
	// @note This is relevant when this component is used outside the customer group popup.
	customerGroupId: PropTypes.string,
	// Name of the customer group to use instead of the one from a selected group item in Redux store
	// @note This is relevant when this component is used outside the customer group popup.
	customerGroupName: PropTypes.string,
	
	// Flag that specifies if the component is in read-only mode
	// @description Read-only mode means that there is no toolbar, no option to select or delete items from the table.
	// @note This is relevant when this component is used outside the customer group popup.
	readOnly: PropTypes.bool,
	// Flag that specifies if a search form is hidden
	hideSearch: PropTypes.bool,
	
	onRemoveFromFileSuccess: PropTypes.func,
};

/**
 * Define component default values for own props
 */
CustomerGroupImportCustomersList.defaultProps = {
	readOnly: false,
	hideSearch: false,
};

export {reducerStoreKey} from "./reducer";

export default connect(
	mapStateToProps, getGlobalActions(actions), null, {forwardRef: true}
)(CustomerGroupImportCustomersList);